"use client";
// packages
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useDeepCompareMemo } from "@react-hookz/web";
import { isAxiosError } from "axios";

// components
import { Form } from "@/shared/components/Form";
import { FormParentContextProvider } from "@/shared/contexts/FormParent";
import { MultiPetDiscountProvider } from "@/shared/contexts/MultiPetDiscount";
import { CANADA_MULTIPET_DISCOUNT_RULE } from "@/shared/utils/MultiPetDiscountUtils";
import { LoaderModal } from "@/shared/components/LoaderModal";
import { QuoteResetModal } from "@/shared/components/QuoteResetModal";

// hooks
import { useModal } from "@/shared/hooks/useModal";
import { UpdateQuoteMutationParams, useQuote } from "@/shared/hooks/useQuote";
import { useOtherValuesMap } from "@/shared/hooks/useOtherValuesStore";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useBreakpoint } from "@/shared/hooks/useBreakpoint";
import { usePetPlans } from "@/shared/hooks/usePetPlans";
import { useStateScopedBreeds } from "@/shared/hooks/useStateScopedBreeds";
import { useDirtyValues } from "@/shared/hooks/useDirtyValues";
import { useDevToolsParams } from "@/shared/hooks/useDevToolsParams";
import { useDataDogViewTracking } from "@/shared/hooks/useDataDogViewTracking";
import { useStatsigUserUpdate } from "@/shared/hooks/useStatsigUserUpdate";
import { useSegmentIdentify } from "@/shared/hooks/useSegmentIdentify";

// utils
import { CaQuoteFormStepIds, getCaQuoteForm } from "./formConfig";
import { CaQuoteFormSteps } from "./formConfigTypes";
import { PtzCaDataUtils } from "./src/utils/PtzCaDataUtils";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import { UIUtils } from "@/shared/utils/UIUtils";
import { StorageUtils } from "@/shared/utils/StorageUtils";
import Strings from "@/shared/utils/Strings.constants";
import { CookieUtils } from "@/shared/utils/CookieUtils";
import { PRIORITY_CODE_DEPRECATED } from "@/shared/types/SpotAPI";

// types
import { Quote, QuoteSchema } from "@/shared/types/Quote.interface";
import { FormStep } from "@/shared/types/Form";
import { ComponentWithUnderwriter } from "@/shared/types/AppRouter";
import { type LogCollection } from "@/shared/utils/ServerFetchUtils";

// config
import { PublicConfig } from "@/shared/PublicConfig";
import ContactSpotError from "@/shared/components/ContactSpotError";
import { useServerLogging } from "@/shared/hooks/useServerLogging";
import { useOtherValuesActions } from "@/shared/hooks/useOtherValuesStore";

const FORM_ID = PublicConfig.PTZ_CA.FORM_ID;
const COOKIE_QUOTE_ID_KEY = PublicConfig.PTZ_CA.COOKIE_QUOTE_ID_KEY;

type CaQuoteFormControllerProps = ComponentWithUnderwriter & {
    serverHeaders: Record<string, string>;
    serverLogs: LogCollection;
};

export function CaQuoteFormController({ underwriter, serverHeaders, serverLogs }: CaQuoteFormControllerProps) {
    const submittingRef = useRef(false);
    const initialValuesLoadedRef = useRef(false);
    const [isFormReady, setIsFormReady] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const modal = useModal();
    const { appState, updateAppState } = useAppLayerContext();
    const currentBreakpoint = useBreakpoint();

    // URL hooks
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();

    const hasPcodeError = appState.asyncErrors?.some(error => error.id === PRIORITY_CODE_DEPRECATED);

    // Removes pcode from URL if it's invalid:
    useEffect(() => {
        if (hasPcodeError) {
            const newURL = UIUtils.deleteQueryParamFromUrl("pcode", searchParams, pathname);
            router.push(newURL, { scroll: false });
        }
    }, [hasPcodeError, searchParams, pathname, router]);

    // quoteId
    const quoteIdParam = UIUtils.getCaseInsensitiveValue(new URLSearchParams(searchParams.toString()), `quoteId`);
    const [quoteId, setQuoteId] = useState(() => quoteIdParam ?? ``);

    // Server state
    const { queryClient, quoteQuery, updateQuote, isQuoteUpdating } = useQuote({
        quoteId,
        underwriter,
        setQuoteId,
        includeBirthProps: true,
        formatRatingZipcode: PtzCaDataUtils.formatRatingZipcode,
        showNetPrice: PublicConfig.PTZ_CA.SHOW_NET_PRICE
    });
    const memoizedQuoteQuery = useDeepCompareMemo(() => (!!quoteQuery ? (quoteQuery as UseQueryResult<Quote, Error>) : undefined), [quoteQuery]);
    const memoizedUpdateQuote = useDeepCompareMemo(
        () => (!!updateQuote ? (updateQuote as UseMutationResult<Quote, Error, UpdateQuoteMutationParams, unknown>) : undefined),
        [updateQuote]
    );

    const { data: quote, isError } = memoizedQuoteQuery || {};

    const otherValuesMap = useOtherValuesMap(FORM_ID);
    const { updateOtherValues, clearAllOtherValues } = useOtherValuesActions(FORM_ID);

    useServerLogging({ serverHeaders, serverLogs });

    // Dev tools params
    useDevToolsParams({
        config: {
            verifyEmailParam: PublicConfig.DEV_PARAMS_GLOBAL.EMAIL_REGISTRATION_CHECK
        }
    });

    // Priority Code
    const priorityCode = useMemo(
        () =>
            UIUtils.getCaseInsensitiveValue(new URLSearchParams(searchParams.toString()), "pcode")?.toLowerCase() ||
            (quote?.discountCode ?? quote?.affiliateCode)?.toLowerCase() ||
            "spot",
        [quote?.affiliateCode, quote?.discountCode, searchParams]
    );

    const petPlansObject = usePetPlans({ quote, underwriter, isEnabled: !isQuoteUpdating, priorityCode });

    // Form state
    const form = useForm<Quote>({
        resolver: zodResolver(QuoteSchema),
        defaultValues: { underwriter, discountCode: priorityCode, affiliateCode: priorityCode, extra: { queryParams: AnalyticsUtils.getQueryParams() } }
    });

    const {
        setValue,
        getValues,
        reset,
        setError,
        control,
        formState: { errors }
    } = form;
    const formValues = useWatch({ control });

    // The ONE breeds catalog for CA (mirrors PTZ-US). CA breeds are province-scoped, so this resolves
    // the province from the parent form's postal (postal → province → breeds) and publishes it via
    // appState so the pet-info dropdowns read the same list. PetInfoWrapper's own step-form instance
    // can't see `ratingZipcode` (it's on the quote root), so it falls back to this published catalog.
    const stateScopedBreeds = useStateScopedBreeds({ underwriter, form, hydratedQuote: quote });
    useEffect(() => {
        updateAppState({ breedsCatalog: stateScopedBreeds });
    }, [stateScopedBreeds, updateAppState]);

    const { resetDirtyValues } = useDirtyValues<Quote>();

    const stepId = useMemo(() => {
        const foundLastStepID = formValues.lastStepID ?? quote?.lastStepID ?? quote?.extra?.lastStepID;
        const defaultStep = foundLastStepID ?? `pets`;
        const lastStepIdDiff = UIUtils.getTimeDiffNow(quote?.extra?.lastStepIDUpdatedAt as string | undefined, "minutes");

        // If the user has a lastStepID and it's been more than X minutes, send them to the step below
        if (foundLastStepID && lastStepIdDiff > PublicConfig.MAX_RETURN_TO_QUOTE_MINUTES) {
            return "coverage";
        }

        // A persisted id that no longer matches a step (e.g. pre-rename "checkout") would fall
        // through to <Form>'s index-0 fallback (pets) — resume those quotes at coverage instead.
        const knownStepIds = Object.values(CaQuoteFormSteps).map(step => step.id);
        if (foundLastStepID && !knownStepIds.includes(foundLastStepID as CaQuoteFormStepIds)) {
            return "coverage";
        }

        return defaultStep as CaQuoteFormStepIds;
    }, [formValues.lastStepID, quote?.extra?.lastStepID, quote?.lastStepID, quote?.extra?.lastStepIDUpdatedAt]);
    const [currentStep, setCurrentStep] = useState<CaQuoteFormStepIds>(stepId);

    // Show reset modal if Quote is in a bad state: invalid quote ID or quote update error.
    // `showResetModal` is a latch: derived here from the same inputs the former effect depended on and
    // set during render, so the modal opens in the render that first sees the error rather than the
    // commit after. It only ever latches on; `resetQuote` is the one path that clears it.
    const shouldShowResetModal = (() => {
        if (!(memoizedUpdateQuote?.isError || memoizedQuoteQuery?.isError)) {
            return false;
        }
        const mutationError = memoizedUpdateQuote?.error;

        // Check if this is a handled async error
        if (isAxiosError(mutationError) && mutationError.response?.data?.error?.message) {
            const apiErrors = mutationError.response.data.error.message;

            // List of error IDs that we handle gracefully (don't need reset modal)
            const handledErrorIds = [
                "invalid-microchip",
                "invalid-pet-name",
                "invalid-pet-birthday",
                "invalid-postal-code",
                "location-not-available",
                "invalid-phone-number",
                "account-exists",
                // A rejected priority code is recovered by the pcode-sync effect (falls back to
                // SPOT), so it must not surface as a reset-quote modal. Mirrors quote-ptz-us.
                PRIORITY_CODE_DEPRECATED
            ];

            // If all errors are handled errors, don't show reset modal
            const allErrorsHandled = Array.isArray(apiErrors) && apiErrors.every(error => handledErrorIds.includes(error.id));

            if (allErrorsHandled) {
                return false;
            }
        }

        // For unhandled errors or query errors, show reset modal
        return true;
    })();
    if (shouldShowResetModal && !showResetModal) {
        setShowResetModal(true);
    }

    useEffect(() => {
        // Clear the optimistic price on step change (mirrors ptz-us) so it doesn't leak between steps.
        updateAppState({ currentStepID: currentStep, calculatedPriceData: undefined });
    }, [updateAppState, currentStep]);

    // Methods

    // `queryClient.clear()` only removes mutations from the cache; the mutation observer that feeds the
    // reset-modal latch keeps returning its last result (`isError: true`) until the mutation itself is
    // reset. Without this the CA latch (no step gate) would re-open the modal on the very next render.
    const resetUpdateQuoteMutation = updateQuote.reset;
    const resetQuote = useCallback(() => {
        queryClient.clear();
        resetUpdateQuoteMutation();
        setShowResetModal(false);
        setQuoteId(``);
        setCurrentStep(`pets`);
        CookieUtils.remove(COOKIE_QUOTE_ID_KEY);
        // Deliberately NOT clearing COOKIE_FINALIZE_STARTED_KEY: its value is quoteId-scoped, so it can't
        // gate the fresh quote this reset starts — but it IS the live double-charge gate for the old quote
        // if its finalize outcome is still unknown, and the customer may navigate back to that quote.
        resetDirtyValues();
        const newSearchParams = new URLSearchParams(searchParams.toString());
        newSearchParams.delete("quoteId");
        const newUrl = `${pathname}?${newSearchParams.toString()}`;
        reset({ underwriter });
        router.push(newUrl);
    }, [pathname, queryClient, reset, resetDirtyValues, resetUpdateQuoteMutation, router, searchParams, underwriter]);

    const updateCurrentStep = useCallback(
        (stepId: CaQuoteFormStepIds, quoteId?: string) => {
            const params = new URLSearchParams(searchParams.toString());
            const _quoteIdInParams = UIUtils.getCaseInsensitiveValue(params, "quoteId");
            // Fall back to the cookie (set synchronously when the quote is created in allowContinue).
            // The Form only forwards the quote value here when allowContinue's return parses against
            // PartialQuoteSchema; the Orca-mapped quote may not, so `quoteId` arg can be undefined.
            const _quoteId = quoteId ?? CookieUtils.get(COOKIE_QUOTE_ID_KEY);

            if (!!_quoteId && !_quoteIdInParams) {
                params.set(`quoteId`, _quoteId);
            }

            const newUrl = `${PublicConfig.BASE_PATH}${pathname}?${params.toString()}`;
            window.history.pushState({ currentStep: stepId }, "", newUrl);

            setCurrentStep(stepId);
            resetDirtyValues();

            if (!!appState.asyncErrors?.length) {
                updateAppState({ asyncErrors: undefined });
            }
        },
        [appState.asyncErrors?.length, pathname, resetDirtyValues, searchParams, updateAppState]
    );

    const handleStepChange = useCallback(
        async (newStepIndex: number, newStep: FormStep<Quote, CaQuoteFormStepIds, keyof Quote>, value?: Quote) => {
            if (!newStep?.id) return;

            updateCurrentStep(newStep.id, value?.id);
        },
        [updateCurrentStep]
    );

    // Form
    const { builderPartnerData } = appState;

    const quoteForm = useMemo(
        () =>
            getCaQuoteForm({
                underwriter,
                updateQuote: setValue,
                updateAppState,
                isUpdating: !!appState?.isQuoteUpdating,
                queryClient,
                quoteId,
                setQuoteId,
                currentStep,
                priorityCode: priorityCode,
                updateCurrentStep,
                builderPartnerData,
                showStepLabel: true
            }),
        [appState?.isQuoteUpdating, currentStep, priorityCode, queryClient, setValue, underwriter, updateAppState, updateCurrentStep, quoteId, builderPartnerData]
    );

    const { steps } = quoteForm;

    const checkStepsRunningRef = useRef(false);
    const [isStepCheckFinished, setIsStepCheckFinished] = useState(false);

    // This effect runs when a quote loads and validates the current quote data against the stepSchema and allowContinue
    // methods of each step until it finds a step that is not valid.  It then sets the lastStepID to the id of the invalid step and calls handleStepChange
    useEffect(() => {
        // Nothing to validate without a saved quote, without steps, or once the quote is finalized:
        // the check is trivially finished (see the early return inside `checkSteps`).
        const hasStepsToCheck = !!quote?.id && !!steps.length && quote?.quoteStatus !== `finalized`;

        const checkSteps = async () => {
            try {
                if (!hasStepsToCheck) return;

                for (const [index, stepEntry] of steps.entries()) {
                    const { stepSchema, id, shouldSkip, allowContinue, isMaxDeterministicStep } = stepEntry;

                    if (index === steps.length - 1) {
                        // if we have reached the last step, we can skip all checks below
                        setValue(`lastStepID`, id);
                        handleStepChange(index, stepEntry, quote);
                        break;
                    }

                    if (shouldSkip) {
                        // if the step shouldSkip boolean is true, we can skip all checks below
                        continue;
                    }

                    if (isMaxDeterministicStep) {
                        // The max deterministic step (Coverage) has no stepSchema, so the generic
                        // "valid + has lastStepID → continue" clause below would walk straight past it to the last step (Checkout).
                        // Mirror the US controller: stop here whenever the quote's own lastStepID is at-or-below this step (or absent), so refreshing on Coverage stays on Coverage.
                        // Only resume *past* it (to Checkout) when the quote's lastStepID is a later step AND it's recent.
                        const maxStepIndex = index;
                        const quoteStepIndex = steps.findIndex(step => step.id === quote?.lastStepID);
                        const lastStepIdDiff = UIUtils.getTimeDiffNow(quote?.extra?.lastStepIDUpdatedAt as string | undefined, "minutes");

                        const noLastStepIdInQuote = !quote?.lastStepID;
                        const quoteLastStepIdIsMaxStep = quoteStepIndex === maxStepIndex;
                        const quoteLastStepTimeDiffExceeded = quoteStepIndex > maxStepIndex && lastStepIdDiff > PublicConfig.MAX_RETURN_TO_QUOTE_MINUTES;
                        const shouldBreak = noLastStepIdInQuote || quoteLastStepIdIsMaxStep || quoteLastStepTimeDiffExceeded;

                        if (shouldBreak) {
                            setValue(`lastStepID`, id);
                            handleStepChange(index, stepEntry, quote);
                            break;
                        }
                    }

                    // check the quote against the current stepSchema if it exists
                    const isValid = stepSchema?.safeParse(quote);

                    if (!!stepSchema && !isValid?.success) {
                        // if the quote data is not valid against the stepSchema, set the lastStepID to the id of the current step
                        setValue(`lastStepID`, id);
                        handleStepChange(index, stepEntry, quote);
                        break;
                    }

                    if ((isValid?.success || !stepSchema) && !!quote?.lastStepID) {
                        // if the quote data is valid against the stepSchema and the quote has a lastStepID,
                        // we can skip all checks below and continue to next step
                        continue;
                    }

                    if (!!allowContinue) {
                        // if the step has an allowContinue method, check if we can continue to the next step
                        const canContinue = await allowContinue(quote, otherValuesMap[id]);
                        if (canContinue) {
                            continue;
                        }
                    }

                    // if allowContinue returns false, set the lastStepID to the id of the current step and call handleStepChange
                    setValue(`lastStepID`, id);
                    handleStepChange(index, stepEntry, quote);
                    break;
                }
            } catch (error) {
                console.error("Error in checkSteps:", error);
            } finally {
                setIsStepCheckFinished(true);
            }
        };

        if (!checkStepsRunningRef.current && isFormReady && !submittingRef.current) {
            checkStepsRunningRef.current = true;
            // `checkSteps` owns the finished latch (its `finally`), on both the trivial and the real path.
            checkSteps();
        }
    }, [handleStepChange, isFormReady, otherValuesMap, quote, setValue, steps]);

    // Sync the step to the derived `stepId` once the landing step check has finished, and again whenever
    // `stepId` changes afterwards. Adjust-during-render keyed on the (stepId, isStepCheckFinished) pair —
    // the same trigger the former effect had — so React re-renders straight into the synced step instead
    // of committing one pass on the previous one.
    const [syncedStepInput, setSyncedStepInput] = useState({ stepId, isStepCheckFinished });
    if (syncedStepInput.stepId !== stepId || syncedStepInput.isStepCheckFinished !== isStepCheckFinished) {
        setSyncedStepInput({ stepId, isStepCheckFinished });
        if (!!stepId && isStepCheckFinished) {
            setCurrentStep(stepId);
        }
    }

    const handleSubmitEffects = async (value: Quote, step: FormStep<Quote, CaQuoteFormStepIds, keyof Quote>): Promise<string | void> => {
        /* For any side effects we need to run on submitting a step */
        if (step.allowSubmit && step.allowSubmit(value, otherValuesMap[step.id])) {
        }
    };

    // Effects

    useEffect(() => {
        const handlePopState = async (event: PopStateEvent) => {
            const stateStepId = window.history.state?.currentStep;
            if (stateStepId) {
                setCurrentStep(stateStepId as CaQuoteFormStepIds);
            } else {
                setCurrentStep(`pets`);
            }
        };

        // Add the async event listener
        window.addEventListener("popstate", handlePopState);

        // Clean up the event listener
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    useEffect(() => {
        // redirect to /thankyou if the quoteStatus is "finalized"

        if (quote?.quoteStatus === `finalized` && !submittingRef.current) {
            const params = new URLSearchParams(searchParams.toString());
            const _quoteIdInParams = UIUtils.getCaseInsensitiveValue(params, "quoteId");
            const _underwriter = UIUtils.getCaseInsensitiveValue(params, "uw");
            const _quoteId = quote?.id;

            if (!!_quoteId && !_quoteIdInParams) {
                params.set(`quoteId`, _quoteId);
            }

            if (!_underwriter) {
                params.set(`uw`, underwriter);
            }

            CookieUtils.remove(COOKIE_QUOTE_ID_KEY);
            // Clear the finalize-in-flight marker on the reload-lands-on-finalized path (this redirect
            // doesn't go through useChasePayment.goToThankYou, which clears it for the in-session path).
            // Only when the marker belongs to THIS quote — an unconditional remove could drop the live
            // double-charge gate of a different quote whose finalize outcome is still unknown.
            if (CookieUtils.get(PublicConfig.PTZ_CA.COOKIE_FINALIZE_STARTED_KEY) === quote?.id) {
                CookieUtils.remove(PublicConfig.PTZ_CA.COOKIE_FINALIZE_STARTED_KEY);
            }

            const newUrl = `${PublicConfig.BASE_PATH}/forms/${FORM_ID}/thankyou?${params.toString()}`;
            router.push(newUrl, { scroll: false });
        }
    }, [quote?.id, quote?.quoteStatus, resetQuote, router, searchParams, underwriter]);

    useEffect(() => {
        // Push underwriter value into AppLayer for use in other components
        if (!!underwriter) {
            updateAppState({ underwriter });
        }
    }, [underwriter, updateAppState]);

    // PRIORITY CODE — mirrors quote-ptz-us. The one-shot ref is load-bearing: the API may strip or
    // reject the URL pcode (e.g. an unknown employer code), and the form is reset from every server
    // response, so without it the mismatch never converges and the quote is PUT in an endless loop.
    const pcodeCheckRunningRef = useRef(false);

    const browserPCode = UIUtils.getCaseInsensitiveValue(new URLSearchParams(searchParams.toString()), "pcode");

    useEffect(() => {
        // If the browser has a pCode and a quote ID exists, we should update the quote with it. Note this means that last pcode wins and overwrites previous pcode, but
        // the API will overrule us if the new pcode has a lesser discount. So there is no guarantee this pcode will stick!
        const updatePcode = async () => {
            try {
                // If the quote already exists, go ahead and update our API with the new pcode. Otherwise, it will be updated when the form
                // has changes in the future.
                if (!!quote?.id) {
                    const currentValues = getValues();
                    const updatedQuote = await memoizedUpdateQuote?.mutateAsync({
                        quote: {
                            ...quote,
                            ...currentValues,
                            discountCode: hasPcodeError ? "SPOT" : browserPCode,
                            affiliateCode: hasPcodeError ? "SPOT" : browserPCode,
                            lastStepID: currentStep
                        }
                    });
                    setValue(`discountCode`, updatedQuote?.discountCode);
                    setValue(`affiliateCode`, updatedQuote?.affiliateCode);
                }
            } catch (error) {
                console.error("Error updating priority code");
            }
        };

        if (
            !pcodeCheckRunningRef.current &&
            isStepCheckFinished &&
            !!browserPCode &&
            (quote?.discountCode?.toLowerCase() ?? quote?.affiliateCode?.toLowerCase()) !== browserPCode?.toLowerCase() &&
            isFormReady &&
            !appState?.isQuoteUpdating &&
            quote?.quoteStatus !== "finalized" &&
            quote?.quoteStatus !== "finalize-processing" &&
            currentStep !== CaQuoteFormSteps.PETS.id
        ) {
            pcodeCheckRunningRef.current = true;
            updatePcode();
        }
    }, [appState?.isQuoteUpdating, browserPCode, currentStep, getValues, hasPcodeError, isFormReady, isStepCheckFinished, memoizedUpdateQuote, quote, setValue]);

    useEffect(() => {
        // Sets initial form values once the quote ID is available
        if (!initialValuesLoadedRef.current && !quoteIdParam) {
            initialValuesLoadedRef.current = true;
            setIsFormReady(true);
        }

        if (!initialValuesLoadedRef.current && quoteId && !!quote?.id) {
            initialValuesLoadedRef.current = true;
            const allValues = getValues();
            const merged = { ...quote, ...allValues, underwriter, extra: { ...quote.extra } };
            reset(merged);
            setIsFormReady(true);
        }
    }, [quoteId, quote, reset, getValues, quoteIdParam, formValues.id, underwriter]);

    useEffect(() => {
        // Here we update the form values from the API data
        if (!!quote?.id) {
            // The server response now has the correct prices on the policy, so clear the optimistic
            // calculatedPriceData. This also re-enables the billing-frequency toggle, which is
            // disabled while calculatedPriceData is set.
            updateAppState({ calculatedPriceData: undefined });
            reset(quote);
            if (PublicConfig.ENVIRONMENT === `development`) {
                StorageUtils.setItem(`dev-quote`, JSON.stringify(quote));
            }
        }
    }, [quote, reset, getValues, updateAppState]);

    useEffect(() => {
        if (!!memoizedQuoteQuery) {
            updateAppState({ quoteQuery: memoizedQuoteQuery });
        }
    }, [memoizedQuoteQuery, updateAppState]);

    useEffect(() => {
        if (!!memoizedUpdateQuote) {
            updateAppState({ updateQuote: memoizedUpdateQuote });
        }
    }, [memoizedUpdateQuote, updateAppState]);

    useEffect(() => {
        updateAppState({ isQuoteUpdating: isQuoteUpdating });
    }, [isQuoteUpdating, updateAppState]);

    useEffect(() => {
        updateAppState({ breakpoint: currentBreakpoint });
    }, [currentBreakpoint, updateAppState]);

    useEffect(() => {
        updateAppState({ priorityCode: priorityCode });
    }, [priorityCode, updateAppState]);

    useEffect(() => {
        updateAppState({ isAnnualBilling: formValues?.billingInfo?.frequency === "yearly" });
    }, [formValues?.billingInfo?.frequency, updateAppState]);

    useEffect(() => {
        if (!!quote?.id && !!petPlansObject) {
            updateAppState({ petPlansObject });
        }
    }, [petPlansObject, quote?.id, updateAppState]);

    const shouldShowLoader = (!!quote?.id && !formValues.lastStepID && !isStepCheckFinished) || petPlansObject.isPetPlansUpdating;

    useEffect(() => {
        if (shouldShowLoader) {
            updateAppState({ isFormLoaderVisible: true });
        } else {
            updateAppState({ isFormLoaderVisible: false });
        }
    }, [shouldShowLoader, updateAppState]);

    useDataDogViewTracking({ formId: FORM_ID, formStepId: currentStep, isEnabled: isStepCheckFinished });
    useStatsigUserUpdate({ quote: quote });
    useSegmentIdentify(quote);

    return (
        <>
            {appState.showFinalLoader && <LoaderModal />}
            <QuoteResetModal onReset={resetQuote} isVisible={showResetModal} />
            <FormParentContextProvider methods={form}>
                <MultiPetDiscountProvider staticRule={CANADA_MULTIPET_DISCOUNT_RULE}>
                    <Form<CaQuoteFormStepIds, Quote>
                        key={currentStep}
                        form={quoteForm}
                        initialStepID={currentStep}
                        value={formValues}
                        otherValuesMap={otherValuesMap}
                        isUpdating={!!appState?.isFormLoaderVisible}
                        onStepChange={handleStepChange}
                        onSubmitStep={handleSubmitEffects}
                        errorComponent={<ContactSpotError phone={Strings.PTZ_CA.PHONE_NUMBER} hours={UIUtils.getPhoneHours("PTZ_CA")} />}
                    />
                </MultiPetDiscountProvider>
            </FormParentContextProvider>
            {modal.render}
        </>
    );
}
